import { Children, ReactElement, ReactNode, Fragment, isValidElement, useMemo, useState, FC } from 'react';
import { css } from '@emotion/react';
import {
  useFloating,
  offset,
  shift,
  flip,
  autoUpdate,
  useHover,
  FloatingPortal,
  useInteractions,
  safePolygon,
  useDismiss,
} from '@floating-ui/react';
import { motion } from 'motion/react';
import { theme } from '@frontend/theme';
import { AdaptoActions } from '../adapto-actions';

type FloatingActionBarProps = {
  children: ReactNode[];
  disabled?: boolean;
  as?: React.ElementType;
};

type ActionsProps = {
  children: ReactNode;
};

const flattenActions = (actions: ReactNode): ReactNode[] => {
  if (isValidElement(actions)) {
    if (actions.type === Fragment) {
      return Children.toArray(actions.props.children);
    }
    return [actions];
  }
  if (Array.isArray(actions)) {
    return actions.flatMap(flattenActions);
  }
  return [actions];
};

export const FloatingActionBarWrapper: FC<FloatingActionBarProps> = ({ children, disabled = false, as = 'div' }) => {
  const [isActive, setIsActive] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const shouldBeVisible = isActive || isOpen;

  const Component = as;

  const { refs, floatingStyles, context } = useFloating({
    open: shouldBeVisible,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (!shouldBeVisible) {
        AdaptoActions.close();
      }
    },
    placement: 'top-end',
    middleware: [offset(({ rects }) => ({ mainAxis: -(rects.floating.height - 4), crossAxis: -4 })), shift(), flip()],
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context, {
    outsidePress: true,
    escapeKey: true,
  });

  const hover = useHover(context, {
    delay: { open: 0, close: 100 },
    handleClose: safePolygon({
      requireIntent: true,
      blockPointerEvents: false,
    }),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);

  const composedStyle = useMemo(() => {
    return {
      ...floatingStyles,
      transform: `${floatingStyles.transform ?? ''} scale(${shouldBeVisible ? 1 : 0.95})`,
      opacity: shouldBeVisible ? 1 : 0,
      transition: shouldBeVisible ? 'opacity 150ms, transform 150ms' : 'opacity 100ms, transform 100ms',
      animationTimingFunction: shouldBeVisible ? 'ease-out' : 'ease-in',
    };
  }, [floatingStyles, shouldBeVisible]);

  const reactChildren: ReactNode[] = useMemo(() => Children.toArray(children), [children]);

  const actionsContent = useMemo(
    () =>
      reactChildren.find(
        (child): child is ReactElement => isValidElement(child) && (child.type as any)?.displayName === 'Actions'
      ),
    [reactChildren]
  );

  const referenceContent = useMemo(
    () =>
      reactChildren.find(
        (child): child is ReactElement => isValidElement(child) && (child.type as any)?.displayName === 'Content'
      )?.props?.children,
    [reactChildren]
  );

  return (
    <>
      <Component
        ref={disabled ? null : refs.setReference}
        onFocus={disabled ? undefined : () => setIsActive(true)}
        css={{ position: 'relative', display: 'inline-block', width: '100%' }}
        {...(disabled ? {} : getReferenceProps())}
      >
        <Content>{referenceContent}</Content>
      </Component>
      {!disabled && (
        <FloatingPortal>
          <motion.div
            ref={refs.setFloating}
            css={floatingActionBarStyles(shouldBeVisible)}
            style={composedStyle}
            role='toolbar'
            aria-live='polite'
            onPointerEnter={() => setIsActive(true)}
            onPointerLeave={() => setIsActive(false)}
            {...getFloatingProps()}
          >
            <Actions>{actionsContent}</Actions>
          </motion.div>
        </FloatingPortal>
      )}
    </>
  );
};

export const Actions = ({ children }: ActionsProps) => {
  const actions = useMemo(() => flattenActions(children), [children]);
  return (
    <AdaptoActions variant='icon-buttons' iconColor='default' maxActions={3} size='small' enableCloseEvent={true}>
      {actions}
    </AdaptoActions>
  );
};

export const Content = ({ children }: { children: ReactNode }) => {
  return <>{children}</>;
};

const floatingActionBarStyles = (isActive: boolean) => css`
  align-items: center;
  display: inline-flex;
  border-radius: ${theme.borderRadius.small};
  border: 1px solid ${theme.colors.neutral10};
  background-color: ${theme.colors.white};
  color: ${theme.colors.text.default};
  padding: ${theme.spacing(0.5)};
  box-shadow: ${theme.shadows.light};
  cursor: pointer;
  overflow: visible;
  z-index: ${theme.zIndex.modals};
  pointer-events: ${isActive ? 'auto' : 'none'};
`;

FloatingActionBarWrapper.displayName = 'FloatingActionBar';
Actions.displayName = 'Actions';
Content.displayName = 'Content';

const FloatingActionBar = Object.assign(FloatingActionBarWrapper, {
  Actions,
  Content,
});

export { FloatingActionBar };
